import React, { useState } from 'react';
import { Card, CardHeader, CardBody, CardExpandToggler } from '../../../components/card/card.jsx';

/**
 * Component for selecting security scanning tools
 */
const ToolSelector = ({
  scanners,
  selectedTools,
  scannersByCategory,
  availablePresets,
  userRole,
  hasToolPermission,
  onToolSelection,
  onSelectAll,
  onClearAll,
  onApplyPreset,
  isLoading
}) => {
  const [expandedCategories, setExpandedCategories] = useState({
    basic: true,
    web: false,
    network: false,
    advanced: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryIcon = (category) => {
    const icons = {
      cloud: 'fa-cloud',
      security: 'fa-globe',
      network: 'fa-network-wired',
      exploitation: 'fa-cogs',
      reconnaissance: 'fa-search',
      vulnerability: 'fa-bug',
      email: 'fa-envelope',
      performance: 'fa-chart-line',
      other: 'fa-tools'
    };
    return icons[category] || 'fa-tools';
  };

  const getCategoryColor = (category) => {
    const colors = {
      cloud: 'success',
      security: 'primary',
      network: 'info',
      exploitation: 'warning',
      reconnaissance: 'secondary',
      vulnerability: 'danger',
      email: 'secondary',
      performance: 'info',
      web: 'info',
      active: 'success',
      advanced: 'warning',
      other: 'dark'
    };
    return colors[category] || 'secondary';
  };

  const getRoleColor = (role) => {
    const colors = {
      user: 'success',
      superuser: 'warning',
      admin: 'danger'
    };
    return colors[role] || 'secondary';
  };

  function getRoleAbbreviation(role) {
    switch (role) {
      case 'user':
        return 'US';
      case 'superuser':
        return 'SU';
      case 'admin':
        return 'AD';
      default:
        return '';
    }
  }

  const getSelectedCount = () => selectedTools.length;
  const getAvailableCount = () => scanners.filter(s => hasToolPermission(s.requiredRole || 'user')).length;

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <i className="fa fa-tools me-2"></i>
          Tool Selection
          <small className="text-muted ms-2">
            ({getSelectedCount()} of {getAvailableCount()} tools selected)
          </small>
        </div>
        <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={onSelectAll}
              disabled={isLoading}
              title="Select all tools you have permission to use"
            >
              <i className="fa fa-check-square me-1"></i>
              Select All
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={onClearAll}
              disabled={isLoading}
              title="Clear all tool selections"
            >
              <i className="fa fa-square me-1"></i>
              Clear All
            </button>
          </div>
        <CardExpandToggler />
      </CardHeader>
      <CardBody>
        {/* Preset Buttons */}
        <div className="mb-3">
          <div className="d-flex flex-wrap gap-2 mb-2">
            <small className="text-muted align-self-center me-2">Quick Presets:</small>
            {availablePresets.map(([presetName, preset]) => (
              <button
                key={presetName}
                type="button"
                className={`btn btn-sm btn-outline-${getCategoryColor(presetName)}`}
                onClick={() => onApplyPreset(presetName)}
                disabled={isLoading}
                title={preset.description}
              >
                {preset.name}
              </button>
            ))}
          </div>
          
        </div>

        {/* Tool Categories */}
        {Object.entries(scannersByCategory).map(([category, categoryTools]) => {
          const isExpanded = expandedCategories[category];
          const categoryCount = categoryTools.filter(tool => hasToolPermission(tool.requiredRole || 'user')).length;
          const selectedInCategory = categoryTools.filter(tool => selectedTools.includes(tool.id)).length;

          return (
            <div key={category} className="mb-3">
              <div 
                className="d-flex justify-content-between align-items-center p-2 bg-dark rounded cursor-pointer"
                onClick={() => toggleCategory(category)}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <i className={`fa ${getCategoryIcon(category)} me-2 text-${getCategoryColor(category)}`}></i>
                  <strong className="text-capitalize">{category} Tools</strong>
                  <small className="text-muted ms-2">
                    ({selectedInCategory}/{categoryCount} selected)
                  </small>
                </div>
                <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
              </div>

              {isExpanded && (
                <div className="mt-2">
                  <div className="row">
                    {categoryTools.map(tool => {
                      const hasPermission = hasToolPermission(tool.requiredRole || 'user');
                      const isSelected = selectedTools.includes(tool.id);
                      const requiredRole = tool.requiredRole || 'user';

                      return (
                        <div key={tool.id} className="col-md-6 col-lg-4 mb-2">
                          <div className={`card h-100 ${!hasPermission ? 'opacity-50' : ''}`}>
                            <div className="card-body p-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`tool-${tool.id}`}
                                  checked={isSelected}
                                  disabled={!hasPermission || isLoading}
                                  onChange={(e) => onToolSelection(tool.id, e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor={`tool-${tool.id}`}>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <strong className="d-block">{tool.name}</strong>
                                      <small className="text-muted d-block">
                                        {tool.description}
                                      </small>
                                    </div>
                                    <span className={`badge bg-${getRoleColor(requiredRole)} ms-2`} data-bs-toggle="tooltip" data-bs-placement="top" title={requiredRole.toUpperCase()}>
                                      {getRoleAbbreviation(requiredRole)} {/* {requiredRole.toUpperCase()} */}
                                    </span>
                                  </div>
                                  {tool.risks && (
                                    <small className="text-warning d-block mt-1">
                                      <i className="fa fa-exclamation-triangle me-1"></i>
                                      {tool.risks}
                                    </small>
                                  )}
                                  {!hasPermission && (
                                    <small className="text-danger d-block mt-1">
                                      <i className="fa fa-lock me-1"></i>
                                      Requires {requiredRole} role or higher
                                    </small>
                                  )}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Summary */}
        {getSelectedCount() > 0 && (
          <div className="alert alert-info mt-3">
            <div className="d-flex align-items-center">
              <i className="fa fa-info-circle me-2"></i>
              <div>
                <strong>Selected Tools Summary:</strong>
                <div className="mt-1">
                  {selectedTools.map(toolId => {
                    const tool = scanners.find(s => s.id === toolId);
                    return tool ? (
                      <span key={toolId} className="badge bg-secondary me-1 mb-1">
                        {tool.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No tools selected warning */}
        {getSelectedCount() === 0 && (
          <div className="alert alert-warning mt-3">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <strong>No tools selected.</strong> Please select at least one security tool to run the scan.
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ToolSelector;